import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useGTM } from '~/hooks/useGTM';
import { useSearchParams } from '~/hooks/useSearchParams';
import { isNil, isNonEmptyString, isNotNil } from '~/utilities/type-guards';
import { Broadcast } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast';
import {
  ANONYMOUS_USER_FIRST_NAME,
  ANONYMOUS_USER_LAST_NAME,
  REGISTRATION_LOCAL_STORAGE_KEY,
} from '~/singlePageApp/screens/LiveStreamScreen/shared/constants';
import { PreviewBanner } from '~/singlePageApp/screens/LiveStreamScreen/AudienceRegistration/PreviewRegistrationBanner';
import {
  initializeAudienceMemberId,
  isEventEnded,
  setUserDataForEvent,
} from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/utils/eventHelpers';
import { v4 as uuidv4 } from 'uuid';
import { LoadingSpinner } from '~/singlePageApp/components/create/shared/LoadingSpinner';
import { reportError } from '~/utilities/errorReporting';
import { LiveErrorCard } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/components/shared/LiveErrorCard';
import { identity, setLocalStorageData } from '~/utilities/util';
import { differenceInMilliseconds, minutesToMilliseconds } from 'date-fns';
import { EventNotFound } from '~/singlePageApp/screens/LiveStreamScreen/AudienceRouter/EventNotFound';
import { fastHostname, appHost } from '~/constantDefinitions';
import { fetchWithSchema } from '~/utilities/fetchHelpers';
import { useLoadFeatureFlags } from '~/singlePageApp/hooks/useLoadFeatureFlags';
import { useGetLiveEventDataForRouterQuery } from '~/singlePageApp/screens/LiveStreamScreen/AudienceRouter/gql/__gql/GetLiveEventDataForRouter.gql.gen';
import { match, P } from 'ts-pattern';
import type { StoredRegistrationData } from '~/singlePageApp/screens/LiveStreamScreen/AudienceRouter/types/RegistrationData';
import {
  savedRegistrationDataSchema,
  getParsedStoredRegistrationData,
} from '~/singlePageApp/screens/LiveStreamScreen/AudienceRouter/types/RegistrationData';
import { useVerifyContactIdentities } from '~/components/live_analytics/useVerifyContactIdentities';
import { useGetContactIdentities } from '~/components/live_analytics/useGetContactIdentities';
import { useUpdateEmailSendingDomainMutation } from '~/singlePageApp/screens/LiveStreamScreen/AudienceRouter/gql/__gql/UpdateEmailSendingDomain.gql.gen';
import { AnonymousRegistrationCustomizationsProvider } from '../LiveStreamCustomizeScreen/Registration/AnonymousRegistrationCustomizationsProvider';
import { AudienceRegistration } from '../AudienceRegistration';

// eslint-disable-next-line sonarjs/cognitive-complexity
const RouterPage = (): JSX.Element => {
  const [queryParams] = useSearchParams();
  const { hashedId = '' } = useParams();

  const inPreviewMode = isNotNil(queryParams.get('previewRegistration'));
  const previewOnDemandEnabled = isNotNil(queryParams.get('previewOnDemandEnabled'));
  const previewOnDemandDisabled = isNotNil(queryParams.get('previewOnDemandDisabled'));

  const { flags } = useLoadFeatureFlags(['flag_smart_form_v1_identities']);
  const useContactIdentities = flags.flag_smart_form_v1_identities.enabled;

  const storedRegistrationData = getParsedStoredRegistrationData();
  const eventRegistrationData = storedRegistrationData.find((el) => el.eventId === hashedId);

  const [isRegistered, setIsRegistered] = useState(isNotNil(eventRegistrationData));
  const [registeredUid, setRegisteredUid] = useState(
    isNotNil(eventRegistrationData) ? eventRegistrationData.id : null,
  );
  const [previouslyRegistered, setPreviouslyRegistered] = useState(false);
  const [isRegistrationRestricted, setIsRegistrationRestricted] = useState(false);

  const gtmEvent = useGTM({ isAnonymous: true });

  const [updateEmailSendingDomain] = useUpdateEmailSendingDomainMutation();

  const { data, loading, error } = useGetLiveEventDataForRouterQuery({
    variables: { hashedId },
  });

  const accountHashedId = data?.audienceLiveStreamEvent.account.hashedId;

  const [contactIdentitiesToVerify, refreshContactIdentities] = useGetContactIdentities({
    accountHashedId,
    url: window.location.href,
  });
  const {
    registered,
    restricted,
    isLoading: contactIdentityLoading,
  } = useVerifyContactIdentities({
    accountHashedId,
    contactIdentities: contactIdentitiesToVerify,
    embedHashedId: hashedId,
    embedType: 'LiveStreamEvent',
    appHost,
    skip: !useContactIdentities,
  });

  const onRegister = useCallback(
    ({
      firstName,
      lastName,
      email,
      id,
      channelId,
    }: {
      firstName: StoredRegistrationData[number]['firstName'];
      lastName: StoredRegistrationData[number]['lastName'];
      email: StoredRegistrationData[number]['email'];
      id?: StoredRegistrationData[number]['id'];
      channelId?: StoredRegistrationData[number]['channelId'];
    }) => {
      const notInPreviewMode = !inPreviewMode && !previewOnDemandEnabled;
      if (notInPreviewMode && !isRegistered) {
        const registrationUid = match(id)
          .when(isNil, () => initializeAudienceMemberId(hashedId))
          .with(P.string, identity)
          .exhaustive();

        setRegisteredUid(registrationUid);

        const newLocalData = storedRegistrationData.concat({
          id: registrationUid,
          firstName,
          lastName,
          role: 'audience',
          email,
          eventId: isNotNil(hashedId) ? hashedId : '',
          channelId,
        });

        setLocalStorageData(REGISTRATION_LOCAL_STORAGE_KEY, newLocalData);

        setUserDataForEvent(hashedId, {
          id: uuidv4(),
          username: `${firstName} ${lastName}`,
          role: 'audience',
          email,
        });
      }

      if (useContactIdentities && notInPreviewMode && !registered) {
        refreshContactIdentities();
      }

      setIsRegistered(true);
      gtmEvent('w.liveRegistrationFormSubmission');

      void updateEmailSendingDomain({
        variables: {
          input: {
            email,
          },
        },
      });
    },
    [
      gtmEvent,
      hashedId,
      inPreviewMode,
      isRegistered,
      previewOnDemandEnabled,
      storedRegistrationData,
      refreshContactIdentities,
      useContactIdentities,
      registered,
      updateEmailSendingDomain,
    ],
  );

  const onAnonymousRegistration = useCallback(() => {
    if (!inPreviewMode && !isRegistered && !previewOnDemandEnabled) {
      const newLocalData = storedRegistrationData.concat({
        id: isNotNil(hashedId) ? initializeAudienceMemberId(hashedId) : '',
        role: 'audience',
        firstName: ANONYMOUS_USER_FIRST_NAME,
        lastName: ANONYMOUS_USER_LAST_NAME,
        email: '',
        eventId: isNotNil(hashedId) ? hashedId : '',
      });

      setLocalStorageData(REGISTRATION_LOCAL_STORAGE_KEY, newLocalData);
    }

    setIsRegistered(true);
    gtmEvent('w.liveRegistrationFormSubmission');
  }, [
    gtmEvent,
    hashedId,
    inPreviewMode,
    isRegistered,
    previewOnDemandEnabled,
    storedRegistrationData,
  ]);

  const setupRegistration = useCallback(
    async (lookupRegistrationId: string) => {
      const response = await fetchWithSchema(
        `https://${fastHostname}/live_stream_event/${hashedId}/registration/${lookupRegistrationId}`,
        savedRegistrationDataSchema,
      );

      match(response)
        .when(isNil, () => setIsRegistrationRestricted(false))
        .with({ type: 'schemaSuccess' }, ({ data: responseData }) => {
          const { is_registrant_restricted } = responseData;

          if (!isRegistered) {
            const { uid: id, first_name: firstName, last_name: lastName, email } = responseData;

            onRegister({ firstName, lastName, email, id });
          }

          setIsRegistrationRestricted(is_registrant_restricted);
        })
        .with(
          { type: 'fetchError' },
          { type: 'fetchErrorWithResponse' },
          { type: 'schemaError' },
          (errorResponse) => {
            setIsRegistrationRestricted(false);

            reportError(errorResponse.error, {
              pillar: 'create',
              product: 'live',
              message: 'Error fetching registration data by user id',
              type: errorResponse.type,
              hashedId,
              registrationUid: lookupRegistrationId,
              fetchResponse: JSON.stringify(errorResponse.fetchResponse),
            });
          },
        )
        .exhaustive();
    },
    [hashedId, isRegistered, onRegister],
  );

  useEffect(() => {
    setPreviouslyRegistered(isRegistered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const lookupRegistrationId = queryParams.get('ruid') ?? registeredUid;

    if (isNonEmptyString(lookupRegistrationId)) {
      void setupRegistration(lookupRegistrationId);
    }
  }, [hashedId, isRegistered, onRegister, queryParams, registeredUid, setupRegistration]);

  if (isNil(data) || loading) {
    return <LoadingSpinner />;
  }

  if (isNotNil(error)) {
    reportError(error, { pillar: 'create', product: 'live', hashedId });

    return <LiveErrorCard blockingError={error} />;
  }

  const {
    registrationStatus,
    isReadyToAttend,
    lifecycleStatus,
    settings: { onDemandEnabled, preRecorded },
    endedAt,
  } = data.audienceLiveStreamEvent;

  const isOpen = registrationStatus === 'PUBLISHED';
  const isEnded = isEventEnded(lifecycleStatus);
  const showRegistration =
    (isOpen && !(isRegistered || registered)) || (isOpen && !(isEnded || isReadyToAttend));
  const identityRestricted = (isRegistered || registered) && restricted;
  const showEventNotFound =
    ((isNotNil(registeredUid) && isRegistrationRestricted) || identityRestricted) &&
    isReadyToAttend;

  const closeEventReady =
    isEnded &&
    isNotNil(endedAt) &&
    (differenceInMilliseconds(new Date(), new Date(endedAt)) > minutesToMilliseconds(1) ||
      preRecorded);

  const showEventClosed = closeEventReady && !onDemandEnabled;

  if (showEventNotFound) {
    return <EventNotFound />;
  }

  if (showEventClosed || previewOnDemandDisabled) {
    return (
      <>
        {previewOnDemandDisabled && <PreviewBanner />}
        <AnonymousRegistrationCustomizationsProvider eventHashedId={hashedId}>
          <AudienceRegistration
            hashedId={hashedId}
            onRegister={onRegister}
            onAnonymousRegistration={onAnonymousRegistration}
            isRegistered={isRegistered || !!registered}
            eventStatus={lifecycleStatus}
            inPreviewMode={inPreviewMode}
            previewOnDemandDisabled={previewOnDemandDisabled}
            previewOnDemandEnabled={previewOnDemandEnabled}
            showEventClosed={showEventClosed}
            inCustomizePreviewMode={false}
            contactIdentityLoading={contactIdentityLoading}
          />
        </AnonymousRegistrationCustomizationsProvider>
      </>
    );
  }

  // Always show the registration page when in preview mode, or
  // if registration is "open" and the user has not registered.
  // If the user has registered, continue to show the registration
  // page with the success message if the event is not starting
  // within the next 30 minutes.
  if (inPreviewMode || showRegistration || (previewOnDemandEnabled && isOpen)) {
    return (
      <>
        {(inPreviewMode || (previewOnDemandEnabled && isOpen)) && <PreviewBanner />}
        <AnonymousRegistrationCustomizationsProvider eventHashedId={hashedId}>
          <AudienceRegistration
            hashedId={hashedId}
            onRegister={onRegister}
            onAnonymousRegistration={onAnonymousRegistration}
            isRegistered={isRegistered || !!registered}
            eventStatus={lifecycleStatus}
            inPreviewMode={inPreviewMode}
            previewOnDemandEnabled={previewOnDemandEnabled}
            previewOnDemandDisabled={previewOnDemandDisabled}
            showEventClosed={showEventClosed}
            inCustomizePreviewMode={false}
            contactIdentityLoading={contactIdentityLoading}
          />
        </AnonymousRegistrationCustomizationsProvider>
      </>
    );
  }

  // show event room if previewOnDemandEnabled and registration is closed.
  if (previewOnDemandEnabled && !isOpen) {
    return (
      <>
        <PreviewBanner />
        <Broadcast
          userType="audience"
          isNewRegistration={!previouslyRegistered}
        />
      </>
    );
  }

  return (
    <Broadcast
      userType="audience"
      isNewRegistration={!previouslyRegistered}
    />
  );
};

// eslint-disable-next-line import/no-default-export
export default RouterPage;
